import { lazy, type ComponentType, type ReactElement } from "react";
import { Navigate, useParams } from "react-router-dom";
import type { AdminSection, PlatformSectionKey } from "../types";

const MobileShellRoute = lazy(() => import("../mobile/MobileShellRoute"));

function lazyNamedRoute<TProps>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => {
    const module = await loader();
    return { default: module[exportName] as ComponentType<TProps> };
  });
}

const loadTenantSessionRoute = () => import("../pages/tenant/session");
const loadPublicSpaRoutes = () => import("../pages/PublicSpaRoutes");
const PublicHomeRoute = lazyNamedRoute(loadPublicSpaRoutes, "PublicHomeRoute");
const LoginRoute = lazyNamedRoute(loadPublicSpaRoutes, "LoginRoute");
const SignupRoute = lazyNamedRoute(loadPublicSpaRoutes, "SignupRoute");
const OnboardingRoute = lazyNamedRoute(loadPublicSpaRoutes, "OnboardingRoute");
const PrivacyRoute = lazyNamedRoute(loadPublicSpaRoutes, "PrivacyRoute");
const MobileUploadRoute = lazyNamedRoute(loadPublicSpaRoutes, "MobileUploadRoute");
const PublicOutputShareRoute = lazyNamedRoute(loadPublicSpaRoutes, "PublicOutputShareRoute");
const PublicCustomerShareRoute = lazyNamedRoute(loadPublicSpaRoutes, "PublicCustomerShareRoute");
const PublicCustomerProposalRoute = lazyNamedRoute(
  () => import("../features/customer-proposals/PublicCustomerProposalRoute"),
  "PublicCustomerProposalRoute",
);
const PublicProductCatalogRoute = lazyNamedRoute(
  () => import("../features/product-catalog/PublicProductCatalogRoute"),
  "PublicProductCatalogRoute",
);
const PublicXiaohongshuNoteRoute = lazyNamedRoute(
  () => import("../features/xiaohongshu/PublicXiaohongshuNotePage"),
  "PublicXiaohongshuNotePage",
);
const loadPublicShowcaseRoutes = () => import("../features/public-showcase/PublicShowcaseRoutes");
const PublicShowcaseHomeRoute = lazyNamedRoute(loadPublicShowcaseRoutes, "PublicShowcaseHomeRoute");
const PublicShowcaseCatalogRoute = lazyNamedRoute(loadPublicShowcaseRoutes, "PublicShowcaseCatalogRoute");
const PublicShowcaseProductRoute = lazyNamedRoute(loadPublicShowcaseRoutes, "PublicShowcaseProductRoute");
const PublicShowcaseBundlesRoute = lazyNamedRoute(loadPublicShowcaseRoutes, "PublicShowcaseBundlesRoute");
const PublicShowcaseBundleRoute = lazyNamedRoute(loadPublicShowcaseRoutes, "PublicShowcaseBundleRoute");
const RequireTenantSession = lazyNamedRoute<{ section: AdminSection }>(loadTenantSessionRoute, "RequireTenantSession");
const loadPlatformSpaRoutes = () => import("../pages/PlatformSpaRoutes");
const PlatformLoginRoute = lazyNamedRoute(loadPlatformSpaRoutes, "PlatformLoginRoute");
const PlatformRoute = lazyNamedRoute<{ section: PlatformSectionKey | "ai-run-detail" | "company-new" | "company-users" }>(loadPlatformSpaRoutes, "PlatformRoute");
const NotFoundRoute = lazyNamedRoute(() => import("../pages/tenant/not-found"), "NotFoundRoute");

function MobileIndexRedirect() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return <Navigate to={`/m/${tenantSlug ?? ""}/studio`} replace />;
}

export type SpaRouteDefinition = {
  path: string;
  element: ReactElement;
};

export const publicRoutes: SpaRouteDefinition[] = [
  { path: "/", element: <PublicHomeRoute /> },
  { path: "/login", element: <LoginRoute /> },
  { path: "/signup", element: <SignupRoute /> },
  { path: "/onboarding", element: <OnboardingRoute /> },
  { path: "/privacy", element: <PrivacyRoute /> },
  { path: "/mobile-upload", element: <MobileUploadRoute /> },
  { path: "/mu/:code", element: <MobileUploadRoute /> },
  { path: "/s/output/:shareSlug", element: <PublicOutputShareRoute /> },
  { path: "/s/xiaohongshu/:noteId", element: <PublicXiaohongshuNoteRoute /> },
  { path: "/s/customer-share/:shareSlug", element: <PublicCustomerShareRoute /> },
  { path: "/s/proposal/:shareSlug", element: <PublicCustomerProposalRoute /> },
  { path: "/s/catalog/:shareSlug", element: <PublicProductCatalogRoute /> },
  { path: "/e/:enterprisePublicId", element: <PublicShowcaseHomeRoute /> },
  { path: "/e/:enterprisePublicId/products", element: <PublicShowcaseCatalogRoute /> },
  { path: "/e/:enterprisePublicId/p/:productPublicId", element: <PublicShowcaseProductRoute /> },
  { path: "/e/:enterprisePublicId/bundles", element: <PublicShowcaseBundlesRoute /> },
  { path: "/e/:enterprisePublicId/b/:bundlePublicId", element: <PublicShowcaseBundleRoute /> },
];

export const tenantRoutes: SpaRouteDefinition[] = [
  { path: "/admin/:tenantSlug", element: <RequireTenantSession section="overview" /> },
  { path: "/admin/:tenantSlug/business-profile", element: <RequireTenantSession section="businessProfile" /> },
  { path: "/admin/:tenantSlug/customers", element: <RequireTenantSession section="customers" /> },
  { path: "/admin/:tenantSlug/customers/:customerId", element: <RequireTenantSession section="customerDetail" /> },
  { path: "/admin/:tenantSlug/customer-proposals", element: <RequireTenantSession section="customerProposals" /> },
  { path: "/admin/:tenantSlug/customer-proposals/new", element: <RequireTenantSession section="customerProposalDetail" /> },
  { path: "/admin/:tenantSlug/customer-proposals/:proposalId", element: <RequireTenantSession section="customerProposalDetail" /> },
  { path: "/admin/:tenantSlug/product-catalogs", element: <RequireTenantSession section="productCatalogs" /> },
  { path: "/admin/:tenantSlug/product-catalogs/:catalogId", element: <RequireTenantSession section="productCatalogDetail" /> },
  { path: "/admin/:tenantSlug/leads", element: <RequireTenantSession section="leads" /> },
  { path: "/admin/:tenantSlug/leads/:leadId", element: <RequireTenantSession section="leadDetail" /> },
  { path: "/admin/:tenantSlug/notifications", element: <RequireTenantSession section="notifications" /> },
  { path: "/admin/:tenantSlug/knowledge", element: <RequireTenantSession section="knowledge" /> },
  { path: "/admin/:tenantSlug/assets/knowledge/:entryId", element: <RequireTenantSession section="knowledgeDetail" /> },
  { path: "/admin/:tenantSlug/brands", element: <RequireTenantSession section="brands" /> },
  { path: "/admin/:tenantSlug/brands/:brandId", element: <RequireTenantSession section="brandDetail" /> },
  { path: "/admin/:tenantSlug/products", element: <RequireTenantSession section="products" /> },
  { path: "/admin/:tenantSlug/products/new", element: <RequireTenantSession section="productNew" /> },
  { path: "/admin/:tenantSlug/products/:productId/edit", element: <RequireTenantSession section="productEdit" /> },
  { path: "/admin/:tenantSlug/showcase", element: <RequireTenantSession section="showcase" /> },
  { path: "/admin/:tenantSlug/showcase/:showcaseId", element: <RequireTenantSession section="showcase" /> },
  { path: "/admin/:tenantSlug/products/:productId/preview", element: <RequireTenantSession section="productPreview" /> },
  { path: "/admin/:tenantSlug/detail-plans/:planId", element: <RequireTenantSession section="detailPlanning" /> },
  { path: "/admin/:tenantSlug/generation", element: <RequireTenantSession section="generation" /> },
  { path: "/admin/:tenantSlug/generation/style-restoration", element: <RequireTenantSession section="styleRestoration" /> },
  { path: "/admin/:tenantSlug/generation/xiaohongshu", element: <RequireTenantSession section="xiaohongshu" /> },
  { path: "/admin/:tenantSlug/quick-generation", element: <RequireTenantSession section="quickGeneration" /> },
  { path: "/admin/:tenantSlug/generation/space-composition", element: <RequireTenantSession section="spaceCompositionCreate" /> },
  { path: "/admin/:tenantSlug/generation/composed-product", element: <RequireTenantSession section="composedProductPartCreate" /> },
  { path: "/admin/:tenantSlug/generation/video", element: <RequireTenantSession section="generationVideo" /> },
  { path: "/admin/:tenantSlug/generation/material-renewal", element: <RequireTenantSession section="generationMaterialRenewal" /> },
  { path: "/admin/:tenantSlug/tasks", element: <RequireTenantSession section="tasks" /> },
  { path: "/admin/:tenantSlug/outputs", element: <RequireTenantSession section="outputs" /> },
  { path: "/admin/:tenantSlug/outputs/compare", element: <RequireTenantSession section="outputCompare" /> },
  { path: "/admin/:tenantSlug/outputs/:outputId", element: <RequireTenantSession section="outputDetail" /> },
  { path: "/admin/:tenantSlug/settings", element: <RequireTenantSession section="settings" /> },
  { path: "/admin/:tenantSlug/settings/storage", element: <RequireTenantSession section="storage" /> },
  { path: "/admin/:tenantSlug/users", element: <RequireTenantSession section="users" /> },
  { path: "/admin/:tenantSlug/my-billing", element: <RequireTenantSession section="personalBilling" /> },
  { path: "/admin/:tenantSlug/billing", element: <RequireTenantSession section="billing" /> },
  { path: "/admin/:tenantSlug/ai-runs", element: <RequireTenantSession section="aiRuns" /> },
  { path: "/admin/:tenantSlug/ai-runs/:runId", element: <RequireTenantSession section="aiRunDetail" /> },
  // 未匹配到具体子路径时显示已认证 404，而不是静默回退到总览——
  // 静默回退会把权限/资源不存在等真实故障伪装成"正常打开了总览页"。
  // 若产品后续为某个旧网址明确批准单独 redirect，请在此行之上新增具体路由，
  // 并在紧邻的注释里记录批准依据和移除条件，不要合并进这条通配符兜底。
  { path: "/admin/:tenantSlug/*", element: <RequireTenantSession section="notFound" /> }
];

export const mobileRoutes: SpaRouteDefinition[] = [
  { path: "/m/:tenantSlug", element: <MobileIndexRedirect /> },
  { path: "/m/:tenantSlug/studio", element: <MobileShellRoute section="studio" /> },
  { path: "/m/:tenantSlug/studio/style-restoration", element: <MobileShellRoute section="studioStyleRestoration" /> },
  { path: "/m/:tenantSlug/studio/xiaohongshu", element: <MobileShellRoute section="studioXiaohongshu" /> },
  { path: "/m/:tenantSlug/quick-generation", element: <MobileShellRoute section="quickGeneration" /> },
  { path: "/m/:tenantSlug/studio/material-renewal", element: <MobileShellRoute section="studioMaterialRenewal" /> },
  { path: "/m/:tenantSlug/studio/space-composition", element: <MobileShellRoute section="studioSpaceComposition" /> },
  { path: "/m/:tenantSlug/studio/:workflowType", element: <MobileShellRoute section="studioForm" /> },
  { path: "/m/:tenantSlug/products", element: <MobileShellRoute section="products" /> },
  { path: "/m/:tenantSlug/products/new", element: <MobileShellRoute section="productForm" /> },
  { path: "/m/:tenantSlug/products/:productId/edit", element: <MobileShellRoute section="productForm" /> },
  { path: "/m/:tenantSlug/categories", element: <MobileShellRoute section="productCategories" /> },
  { path: "/m/:tenantSlug/styles", element: <MobileShellRoute section="productStyles" /> },
  { path: "/m/:tenantSlug/showcase", element: <MobileShellRoute section="showcase" /> },
  { path: "/m/:tenantSlug/showcase/:productId", element: <MobileShellRoute section="showcasePublish" /> },
  { path: "/m/:tenantSlug/tasks", element: <MobileShellRoute section="tasks" /> },
  { path: "/m/:tenantSlug/customers", element: <MobileShellRoute section="customers" /> },
  { path: "/m/:tenantSlug/customers/new", element: <MobileShellRoute section="customerForm" /> },
  { path: "/m/:tenantSlug/customers/:customerId/edit", element: <MobileShellRoute section="customerForm" /> },
  { path: "/m/:tenantSlug/customers/:customerId", element: <MobileShellRoute section="customerDetail" /> },
  { path: "/m/:tenantSlug/proposals", element: <MobileShellRoute section="proposals" /> },
  { path: "/m/:tenantSlug/proposals/new", element: <MobileShellRoute section="proposalWorkspace" /> },
  { path: "/m/:tenantSlug/proposals/:proposalId", element: <MobileShellRoute section="proposalWorkspace" /> },
  { path: "/m/:tenantSlug/leads", element: <MobileShellRoute section="leads" /> },
  { path: "/m/:tenantSlug/leads/:leadId", element: <MobileShellRoute section="leadDetail" /> },
  { path: "/m/:tenantSlug/outputs", element: <MobileShellRoute section="outputs" /> },
  { path: "/m/:tenantSlug/outputs/:outputId", element: <MobileShellRoute section="outputDetail" /> },
  { path: "/m/:tenantSlug/me", element: <MobileShellRoute section="me" /> },
];

export const platformRoutes: SpaRouteDefinition[] = [
  { path: "/platform/login", element: <PlatformLoginRoute /> },
  { path: "/platform", element: <PlatformRoute section="overview" /> },
  { path: "/platform/admins", element: <PlatformRoute section="admins" /> },
  { path: "/platform/ai-runs", element: <PlatformRoute section="ai-runs" /> },
  { path: "/platform/ai-runs/:runId", element: <PlatformRoute section="ai-run-detail" /> },
  { path: "/platform/statistics", element: <PlatformRoute section="statistics" /> },
  { path: "/platform/pricing", element: <PlatformRoute section="pricing" /> },
  { path: "/platform/external-mcp", element: <PlatformRoute section="external-mcp" /> },
  { path: "/platform/usage", element: <PlatformRoute section="usage" /> },
  { path: "/platform/billing", element: <PlatformRoute section="billing" /> },
  { path: "/platform/companies", element: <PlatformRoute section="companies" /> },
  { path: "/platform/mini-programs", element: <PlatformRoute section="mini-programs" /> },
  { path: "/platform/member-accounts", element: <PlatformRoute section="member-accounts" /> },
  { path: "/platform/companies/new", element: <PlatformRoute section="company-new" /> },
  { path: "/platform/companies/:tenantId/users", element: <PlatformRoute section="company-users" /> },
  { path: "/platform/notifications", element: <PlatformRoute section="notifications" /> },
  { path: "/platform/research", element: <PlatformRoute section="research" /> },
  { path: "/platform/storage", element: <PlatformRoute section="storage" /> },
  { path: "/platform/models", element: <PlatformRoute section="models" /> },
  { path: "/platform/services", element: <PlatformRoute section="services" /> },
  { path: "/platform/runtime-policies", element: <PlatformRoute section="runtime-policies" /> },
  { path: "/platform/content-safety", element: <PlatformRoute section="content-safety" /> },
  { path: "/platform/audit", element: <PlatformRoute section="audit" /> },
  { path: "/platform/settings", element: <PlatformRoute section="settings" /> },
  { path: "/platform/generation-tasks", element: <PlatformRoute section="generation-tasks" /> },
  // 同上：平台后台未匹配到具体子路径时也显示已认证 404，不静默回退到总览。
  // 目前代码库里没有找到任何产品明确批准需要保留的旧平台网址；
  // 如后续出现，请在此行之上新增具体路由并记录批准依据和移除条件。
  { path: "/platform/*", element: <PlatformRoute section="notFound" /> }
];

export const spaRoutes: SpaRouteDefinition[] = [
  ...publicRoutes,
  ...tenantRoutes,
  ...mobileRoutes,
  ...platformRoutes,
  { path: "*", element: <NotFoundRoute /> }
];
